import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import { scale as initialScale, fullWidth } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';

import TouchableElement from '@components/common/touchableElement';
import { isPhone, isWeb } from '@helpers';
import { updateImageMetadata } from '@reducers/imageHandling';

import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import useManageSuggestion from './hook/useManageSuggestion';
import { TEXT_MAPPING, parseSuggestionItem } from './helpers';

const SuggestionComponent = ({
  C,
  suggestion,
  setSuggestion,
  searchRef,
  width,
  top,
  attachMode,
  attachImageId,
  updateImageMetadata,
  isPopupMode,
  onItemSelect,
  scale = initialScale,
  font = initialFont,
}) => {
  const { filteredSuggestion, shouldShowShadow, getContainerStyle, handleSuggestionPress } = useManageSuggestion({
    suggestion,
    setSuggestion,
    searchRef,
    width,
    top,
    scale,
    attachMode,
    attachImageId,
    updateImageMetadata,
    isPopupMode,
    onItemSelect,
  });

  const IS_NARROW_WEB = !isPhone || shouldShowShadow;

  return (
    <View testID={TEST_IDS.searchScreen.suggestionContainer} style={getContainerStyle()}>
      <View
        style={[
          shouldShowShadow && getBoxShadowStyle({ type: 'Field' }, C),
          {
            width: isPopupMode ? 'auto' : width ? width : fullWidth - scale(isPhone && !IS_NARROW_WEB ? 0 : 36),
            backgroundColor: C('Background/Light/Field'),
            borderRadius: scale(20),
          },
        ]}
      >
        <FlashList
          data={filteredSuggestion}
          keyboardShouldPersistTaps={'handled'}
          style={{
            flex: 1,
            minHeight: filteredSuggestion.length > 0 ? scale(42 * filteredSuggestion.length) : 0,
          }}
          contentContainerStyle={{
            flexGrow: 1,
          }}
          keyExtractor={(item, index) => `suggestion-${index}-${item.text}`}
          renderItem={({ item: { text, lookupKeyFields }, index }) => {
            const parts = text.split('<p>');
            const { type } = parseSuggestionItem(lookupKeyFields, text);

            return (
              <TouchableElement
                testID={TEST_IDS.searchScreen.suggestionItem}
                onPress={(e) => handleSuggestionPress(e, lookupKeyFields, text)}
                useOpacity={true}
                activeOpacity={1}
                hitSlop={0}
              >
                {({ boxStyle, pressHoverColor }) => (
                  <View style={[boxStyle]}>
                    <View
                      style={[
                        boxStyle,
                        isWeb && {
                          borderTopLeftRadius: scale(index === 0 ? SCREEN_BORDER_RADIUS * 2 : 0),
                          borderTopRightRadius: scale(index === 0 ? SCREEN_BORDER_RADIUS * 2 : 0),
                          borderBottomLeftRadius: scale(index === filteredSuggestion.length - 1 ? SCREEN_BORDER_RADIUS * 2 : 0),
                          borderBottomRightRadius: scale(index === filteredSuggestion.length - 1 ? SCREEN_BORDER_RADIUS * 2 : 0),
                        },
                        !isWeb && {
                          borderRadius: scale(SCREEN_BORDER_RADIUS),
                        },
                        {
                          paddingHorizontal: scale(18),
                          justifyContent: 'center',
                          backgroundColor: C(pressHoverColor('Background / Light / Field')),
                        },
                      ]}
                    >
                      <View
                        style={[
                          {
                            height: scale(42),
                            justifyContent: 'center',
                            borderBottomWidth: filteredSuggestion.length - 1 === index ? 0 : StyleSheet.hairlineWidth,
                            borderBottomColor: C('Hairline/Light/No trans'),
                          },
                        ]}
                      >
                        {parts.length === 1 ? (
                          <Text numberOfLines={1}>
                            <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(TEXT_MAPPING[type])} </Text>
                            <Text style={[font['Subhead 1'], { color: C('System/Light/Blue') }]}>{parts[1]}</Text>
                          </Text>
                        ) : (
                          <Text numberOfLines={1}>
                            <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(TEXT_MAPPING[type])} </Text>
                            <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]}>{parts[0]}</Text>
                            <Text style={[font['Subhead 1'], { color: C('System/Light/Blue') }]}>{parts[1]}</Text>
                            <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]}>{parts[2]}</Text>
                          </Text>
                        )}
                      </View>
                    </View>
                  </View>
                )}
              </TouchableElement>
            );
          }}
        />
      </View>
    </View>
  );
};

export default connect(null, { updateImageMetadata })(SuggestionComponent);
